import { useIsMutating } from "@tanstack/react-query";
import cn from "classnames";
import { StaffManagerPermissions, useProfile } from "modules/common";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Tooltip } from "react-tooltip";
import { Loader, Pagination } from "ui";

import {
  AiReviewItem,
  AiReviewSweepInfo,
  EAiReviewStatus
} from "../../aiReview.types";
import {
  AI_REVIEW_UPDATE_MUTATION_KEY,
  useGetReviewItems,
  useUpdateReviewItem
} from "../../apis";
import { AI_REVIEW } from "../../constants/review";
import {
  AI_REVIEW_TABS,
  AiReviewTab,
  reviewActionsFor,
  reviewConversationPath,
  reviewCorrectionPath,
  reviewInboundLabelKey,
  reviewReasonKeys,
  reviewScoreLevel,
  reviewSeenLater,
  reviewTabStatus,
  reviewTicketBadge
} from "../../helpers/review";

import styles from "./AIReview.module.scss";

// One react-tooltip instance for the page; the reason chips carry their
// details in data-tooltip-content, one per line.
const TOOLTIP_ID = "ai-review-tooltip";

const TAB_LABEL_KEY: Record<AiReviewTab, string> = {
  open: "aiReview.tab.open",
  reviewed: "aiReview.tab.reviewed",
  dismissed: "aiReview.tab.dismissed"
};

const formatDateTime = (iso: string | null): string => {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString(undefined, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  });
};

const SweepLines = ({ sweep }: { sweep: AiReviewSweepInfo }) => {
  const { t } = useTranslation();
  return (
    <>
      <p className={styles.intro}>
        {t("aiReview.intro", {
          every: sweep.everyMinutes,
          delay: sweep.delayMinutes
        })}
      </p>
      <p className={styles.muted}>
        {sweep.lastRunAt
          ? t("aiReview.lastRun", { date: formatDateTime(sweep.lastRunAt) })
          : t("aiReview.neverRun")}
        {sweep.enabled ? null : (
          <span className={styles.sweepOff}> {t("aiReview.sweepOff")}</span>
        )}
      </p>
    </>
  );
};

const ReasonChips = ({ item }: { item: AiReviewItem }) => {
  const { t } = useTranslation();
  return (
    <div className={styles.chips}>
      {item.reasons.map((reason, index) => {
        const keys = reviewReasonKeys(reason);
        if (!keys) return null;
        const lines = keys.details.map((key) => t(key));
        if (reviewSeenLater(item, reason)) {
          lines.push(t("aiReview.seenInNextTurn"));
        }
        const tooltip = lines.length > 0 ? lines.join("\n") : undefined;
        return (
          <span
            key={`${reason.code}-${index}`}
            className={styles.chip}
            data-tooltip-id={tooltip ? TOOLTIP_ID : undefined}
            data-tooltip-content={tooltip}
          >
            {t(keys.title)}
          </span>
        );
      })}
    </div>
  );
};

// "Ticket already open": an AI ticket already covers this reply. Opens it on
// the AI tickets page, in a new tab so the list stays where it was.
const TicketBadge = ({ item }: { item: AiReviewItem }) => {
  const { t } = useTranslation();
  const badge = reviewTicketBadge(item);
  if (!badge) return null;
  return (
    <Link
      to={badge.path}
      target="_blank"
      rel="noopener noreferrer"
      className={cn(styles.ticketBadge, {
        [styles.ticketBadgeHandled]: badge.handled
      })}
      data-tooltip-id={TOOLTIP_ID}
      data-tooltip-content={t(badge.tooltipKey)}
    >
      {t("aiReview.ticket.badge")}
    </Link>
  );
};

const ReviewRow = ({
  item,
  showLesson,
  canManage,
  onUpdate
}: {
  item: AiReviewItem;
  showLesson: boolean;
  canManage: boolean;
  onUpdate: (status: EAiReviewStatus) => void;
}) => {
  const { t } = useTranslation();
  // A status change of this row is running (another row may change meanwhile).
  const busy =
    useIsMutating({
      mutationKey: AI_REVIEW_UPDATE_MUTATION_KEY,
      predicate: (mutation) =>
        (mutation.state.variables as { id?: string } | undefined)?.id ===
        item.id
    }) > 0;

  const level = reviewScoreLevel(item.score);
  const inboundLabelKey = reviewInboundLabelKey(item);
  const linkClass = (muted: boolean) =>
    cn(styles.action, {
      [styles.actionMuted]: muted,
      [styles.actionDisabled]: busy
    });

  return (
    <tr className={cn({ [styles.rowBusy]: busy })}>
      <td>
        <span
          className={cn(styles.score, styles[`score_${level}`])}
          title={t(`aiReview.score.${level}`)}
        >
          {item.score}
        </span>
      </td>
      <td>
        <ReasonChips item={item} />
        <TicketBadge item={item} />
      </td>
      <td className={styles.dateCell}>{formatDateTime(item.turnAt)}</td>
      <td>
        {inboundLabelKey ? (
          <span className={styles.placeholder}>{t(inboundLabelKey)}</span>
        ) : (
          <span className={styles.excerpt}>{item.inboundExcerpt || "—"}</span>
        )}
      </td>
      <td>
        {item.replyExcerpt ? (
          <span className={styles.excerpt}>{item.replyExcerpt}</span>
        ) : (
          <span className={styles.placeholder}>{t("aiReview.noReply")}</span>
        )}
      </td>
      {showLesson ? (
        <td>
          <div className={styles.lessonCell}>
            <span className={styles.lessonTitle}>
              {item.lesson?.title || "—"}
            </span>
            {item.reviewedAt ? (
              <span className={styles.subLine}>
                {t("aiReview.reviewedOn", {
                  date: formatDateTime(item.reviewedAt)
                })}
              </span>
            ) : null}
          </div>
        </td>
      ) : null}
      <td>
        <div className={styles.actions}>
          {reviewActionsFor(item, canManage).map((action) => {
            switch (action) {
              case "open":
                return (
                  <Link
                    key={action}
                    to={reviewConversationPath(item)}
                    className={linkClass(true)}
                    aria-disabled={busy}
                  >
                    {t("aiReview.action.open")}
                  </Link>
                );
              case "createLesson":
                return (
                  <Link
                    key={action}
                    to={reviewCorrectionPath(item)}
                    className={linkClass(false)}
                    aria-disabled={busy}
                  >
                    {t("aiReview.action.createLesson")}
                  </Link>
                );
              case "markReviewed":
                return (
                  <button
                    key={action}
                    type="button"
                    className={styles.action}
                    disabled={busy}
                    onClick={() => onUpdate(EAiReviewStatus.Reviewed)}
                  >
                    {t("aiReview.action.markReviewed")}
                  </button>
                );
              case "dismiss":
                return (
                  <button
                    key={action}
                    type="button"
                    className={styles.action}
                    disabled={busy}
                    onClick={() => onUpdate(EAiReviewStatus.Dismissed)}
                  >
                    {t("aiReview.action.dismiss")}
                  </button>
                );
              case "reopen":
                return (
                  <button
                    key={action}
                    type="button"
                    className={cn(styles.action, styles.actionMuted)}
                    disabled={busy}
                    onClick={() => onUpdate(EAiReviewStatus.Open)}
                  >
                    {t("aiReview.action.reopen")}
                  </button>
                );
              default:
                return null;
            }
          })}
        </div>
      </td>
    </tr>
  );
};

// "To review": the WhatsApp AI replies the backend sweep found suspicious
// (no AI involved), by tab. From a reply, "Create a lesson" opens the
// conversation with the correction panel on it; "Nothing to fix" dismisses it.
// Handling an item needs the "Manage AI lessons" permission (the backend
// checks it again). Until the backend serving the page is deployed, its route
// answers 404 and the page shows a quiet notice instead.
const AIReview = () => {
  const { t } = useTranslation();
  const { user } = useProfile({ enabled: false });
  const canManage = Boolean(
    user.role === "Super" ||
    user.permissions?.includes(StaffManagerPermissions.ManageAiLessons)
  );

  const [tab, setTab] = useState<AiReviewTab>("open");
  const [page, setPage] = useState(1);

  const {
    items,
    meta,
    counts,
    sweep,
    data,
    isLoading,
    isError,
    isPreviousData,
    isUnavailable
  } = useGetReviewItems({
    status: reviewTabStatus(tab),
    page,
    take: AI_REVIEW.PAGE_SIZE
  });
  const updateItem = useUpdateReviewItem();

  const selectTab = (next: AiReviewTab) => {
    setTab(next);
    setPage(1);
  };

  // The last item of the last page moved to another tab: go back a page.
  useEffect(() => {
    if (meta && meta.pageCount > 0 && page > meta.pageCount) {
      setPage(meta.pageCount);
    }
  }, [meta, page]);

  const handleUpdate = (item: AiReviewItem, status: EAiReviewStatus) =>
    updateItem.mutate({ id: item.id, status });

  const showLesson = tab === "reviewed";

  const renderBody = () => {
    if (isLoading && !data) {
      return (
        <div className={styles.loaderWrap}>
          <Loader />
        </div>
      );
    }
    if (isError && !data) {
      return <div className={styles.notice}>{t("aiReview.loadFailed")}</div>;
    }
    // The empty list is still the previous tab's or page's (keepPreviousData):
    // the new one's empty message would be a guess until its rows arrive.
    if (items.length === 0 && isPreviousData) {
      return (
        <div className={styles.loaderWrap}>
          <Loader />
        </div>
      );
    }
    if (items.length === 0) {
      return (
        <div className={styles.empty}>
          {t(`aiReview.empty.${tab}`, { every: sweep?.everyMinutes })}
        </div>
      );
    }
    return (
      <div
        className={cn(styles.tableWrapper, {
          [styles.tableStale]: isPreviousData
        })}
      >
        <table className={styles.table}>
          <thead>
            <tr>
              <th>{t("aiReview.column.score")}</th>
              <th>{t("aiReview.column.reasons")}</th>
              <th>{t("aiReview.column.when")}</th>
              <th>{t("aiReview.column.customer")}</th>
              <th>{t("aiReview.column.reply")}</th>
              {showLesson ? <th>{t("aiReview.column.lesson")}</th> : null}
              <th>{t("aiReview.column.actions")}</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <ReviewRow
                key={item.id}
                item={item}
                showLesson={showLesson}
                canManage={canManage}
                onUpdate={(status) => handleUpdate(item, status)}
              />
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  if (isUnavailable) {
    return (
      <div className={styles.page}>
        <h1 className={styles.title}>{t("aiReview.title")}</h1>
        <div className={styles.notice}>{t("aiReview.unavailable")}</div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <h1 className={styles.title}>{t("aiReview.title")}</h1>
        {sweep ? <SweepLines sweep={sweep} /> : null}
      </div>

      <div className={styles.card}>
        <div className={styles.tabs}>
          {AI_REVIEW_TABS.map((key) => (
            <button
              key={key}
              type="button"
              className={cn(styles.tab, { [styles.tabActive]: tab === key })}
              aria-pressed={tab === key}
              onClick={() => selectTab(key)}
            >
              {t(TAB_LABEL_KEY[key])}
              <span
                className={cn(styles.tabCount, {
                  [styles.tabCountActive]: tab === key
                })}
              >
                {(counts?.[key] ?? 0).toLocaleString()}
              </span>
            </button>
          ))}
        </div>

        {!canManage ? (
          <div className={styles.notice}>{t("aiReview.noPermission")}</div>
        ) : null}

        {renderBody()}

        <Pagination
          currentPage={page}
          onChange={setPage}
          totalItems={meta?.itemCount ?? 0}
          perPage={AI_REVIEW.PAGE_SIZE}
        />
      </div>

      <Tooltip
        id={TOOLTIP_ID}
        place="top"
        opacity={1}
        offset={6}
        className={styles.tooltip}
      />
    </div>
  );
};

export default AIReview;
